import { forwardRef } from 'react'
import classNames from 'classnames'

import { Typography, TypographyProps, type WeightHeadingType } from '.'

export type HeadingProps = Omit<TypographyProps, 'variant'> & {
  size?: HeadingSizeType
}

export type HeadingSizeType = '2xl' | 'xl' | 'lg' | 'md' | 'sm' | 'xs' | '2xs'

const sizeToHeadingType: Record<HeadingSizeType, React.ElementType> = {
  '2xl': 'h1',
  xl: 'h1',
  lg: 'h2',
  md: 'h3',
  sm: 'h4',
  xs: 'h5',
  '2xs': 'h6'
}

const sizeToStyle: Record<HeadingSizeType, React.ComponentProps<'h1'>['className']> = {
  '2xl': 'text-5xl tracking-[-0.03rem] leading-[3.5rem]', // 48px
  xl: 'text-4xl tracking-[-0.0225rem]', // 36px
  lg: 'text-[2rem] leading-[2.75rem]', // 32px
  md: 'text-2xl', // 24px
  sm: 'text-xl', // 20px
  xs: 'text-lg leading-[1.5rem]', // 18px
  '2xs': 'text-base' // 16px
}

const weightToStyle: Record<WeightHeadingType, React.ComponentProps<'h1'>['className']> = {
  black: 'font-black',
  bold: 'font-bold',
  regular: 'font-normal',
  light: 'font-light'
}

/**
 * @group Components
 * @category Atoms
 */
export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ className, weight = 'bold', size = 'xl', children, ...props }, ref) => {
    const HeadingComponent = sizeToHeadingType[size]
    const sizeStyle = sizeToStyle[size]
    const weightStyle = weightToStyle[weight]

    return (
      <Typography
        Component={HeadingComponent}
        className={classNames('font-gelica', sizeStyle, weightStyle, className)}
        ref={ref}
        {...props}
      >
        {children}
      </Typography>
    )
  }
)

Heading.displayName = 'Heading'
